if (!customElements.get('marame-reviews')) {
  customElements.define('marame-reviews', class MarameReviews extends HTMLElement {
    connectedCallback() {
      this.disconnectedCallback();
      this.dialog = this.querySelector('dialog');
      this.inline = this.querySelector('[data-reviews-inline]');
      this.widget = this.querySelector('[data-reviews-widget]');
      this.mount = this.querySelector('[data-reviews-mount]');
      this.loading = this.querySelector('[data-reviews-loading]');
      this.slow = this.querySelector('[data-reviews-slow]');
      if (!this.dialog?.showModal || !this.widget || !this.inline) return;
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.dataset.enhanced = 'true';
      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-marame-reviews]');
        if (trigger && trigger.dataset.productId === this.dataset.productId) {
          if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
          if (this.show(trigger)) event.preventDefault();
        }
        if (this.dialog.open && this.widget.contains(event.target) && event.target.closest('.jdgm-write-rev-link, .jdgm-rev__pic-img')) {
          // Let Judge.me receive the original click after its widget leaves our modal.
          this.finishHide(false, true);
        }
      }, { ...options, capture: true });
      this.addEventListener('click', event => {
        if (event.target.closest('[data-reviews-close]')) this.hide();
        if (event.target.closest('[data-reviews-fallback]')) this.viewInline();
      }, options);
      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.hide(); }, options);
      this.dialog.addEventListener('close', () => { if (!this.dialog.open) this.release(); }, options);
      this.dialog.addEventListener('pointerdown', event => { this.backdropDown = this.isBackdrop(event); }, options);
      this.dialog.addEventListener('click', event => {
        if (this.backdropDown && this.isBackdrop(event)) this.hide();
        this.backdropDown = false;
      }, options);
      this.widgetObserver = new MutationObserver(() => this.checkReady());
      this.widgetObserver.observe(this.widget, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'style', 'class'] });
      this.checkReady();
      this.media = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.media.addEventListener?.('change', () => {
        if (!this.media.matches) return;
        if (this.closing) this.finishHide(); else this.stopMotion();
      }, options);
    }

    disconnectedCallback() {
      this.events?.abort(); this.widgetObserver?.disconnect();
      this.finishHide(false);
      if (this.focusFrame) cancelAnimationFrame(this.focusFrame);
      this.focusFrame = null;
    }

    show(trigger) {
      if (!this.dialog?.showModal || document.querySelector('dialog[open]:not(.marame-reviews__dialog)')) return false;
      if (this.dialog.open && !this.closing) return true;
      const start = this.dialog.open ? this.sheetPosition() : null;
      this.stopMotion();
      if (this.focusFrame) cancelAnimationFrame(this.focusFrame);
      this.focusFrame = null;
      this.openedBy = trigger;
      this.mount.append(this.widget);
      this.inline.removeAttribute('data-force-inline');
      if (!this.dialog.open) this.dialog.showModal();
      document.body.classList.add('marame-reviews-open');
      trigger?.setAttribute('aria-expanded', 'true');
      trigger?.setAttribute('aria-controls', this.dialog.id);
      trigger?.setAttribute('aria-haspopup', 'dialog');
      this.querySelector('[data-reviews-close]').focus({ preventScroll: true });
      this.loading.hidden = false; this.slow.hidden = true;
      clearTimeout(this.slowTimer);
      this.checkReady();
      if (!this.loading.hidden) this.slowTimer = setTimeout(() => {
        if (this.dialog.open && !this.loading.hidden) { this.loading.hidden = true; this.slow.hidden = false; }
      }, 10000);
      this.animateSheet(false, start);
      return true;
    }

    checkReady() {
      if (!this.dialog?.open) return;
      const content = [...this.widget.querySelectorAll('button, select, input, [role="button"], .jdgm-rev__body, .jdgm-rev-widg__summary-text, .jm-no-reviews-state__text')];
      const ready = content.some(element => !element.closest('.jdgm-legacy-widget-content, [hidden]') && element.getClientRects().length && window.getComputedStyle(element).visibility !== 'hidden');
      if (ready) { this.loading.hidden = true; this.slow.hidden = true; clearTimeout(this.slowTimer); }
    }

    stopMotion() {
      this.run = (this.run || 0) + 1;
      clearTimeout(this.closeTimer);
      this.animation?.cancel(); this.animation = null;
      this.closing = false;
      this.dialog?.removeAttribute('data-closing');
    }

    sheetAxis() {
      return window.matchMedia?.('(min-width: 1024px)')?.matches ? 'X' : 'Y';
    }

    sheetPosition() {
      if (!window.DOMMatrix || !window.getComputedStyle) return null;
      const transform = window.getComputedStyle(this.dialog).transform;
      if (!transform || transform === 'none') return 0;
      const axis = this.sheetAxis();
      const extent = this.dialog.getBoundingClientRect()[axis === 'X' ? 'width' : 'height'];
      return extent ? 100 * new window.DOMMatrix(transform)[axis === 'X' ? 'm41' : 'm42'] / extent : null;
    }

    animateSheet(exiting, start) {
      if (!this.dialog.animate || this.media.matches) return null;
      const axis = this.sheetAxis();
      const alpha = 34 / (2 * 0.9), omega = Math.sqrt(alpha * alpha - 300 / 0.9);
      const slow = -alpha + omega, fast = -alpha - omega;
      const remaining = time => (-fast * Math.exp(slow * time) + slow * Math.exp(fast * time)) / (slow - fast);
      const frames = [];
      const from = start ?? (exiting ? 0 : 100), to = exiting ? 100 : 0;
      let duration = 0;
      for (let step = 0; step <= 100; step++) {
        const time = step / 100, rest = remaining(time);
        frames.push({ transform: `translate${axis}(${to + (from - to) * rest}%)` });
        duration = step * 10;
        if (time > 0.25 && Math.abs(rest) < 0.001 && Math.abs(remaining(time + .01) - rest) < .0001) break;
      }
      frames[frames.length - 1] = { transform: `translate${axis}(${exiting ? 100 : 0}%)` };
      this.dialog.setAttribute('data-spring-motion', '');
      this.animation = this.dialog.animate(frames, { duration, easing: 'linear', fill: 'both' });
      this.animation.finished?.catch(() => {});
      return { animation: this.animation, duration };
    }

    hide() {
      if (!this.dialog.open || this.closing) return;
      const start = this.sheetPosition();
      this.stopMotion(); this.closing = true;
      this.dialog.setAttribute('data-closing', '');
      const run = this.run, motion = this.animateSheet(true, start);
      if (!motion) { this.finishHide(); return; }
      const finish = () => { if (run === this.run && this.closing) this.finishHide(); };
      motion.animation.finished?.then(finish, finish);
      this.closeTimer = setTimeout(finish, motion.duration + 120);
    }

    finishHide(returnFocus = true, forceInline = false) {
      const wasOpen = this.dialog?.open;
      this.stopMotion();
      if (forceInline) this.inline?.setAttribute('data-force-inline', '');
      if (wasOpen) this.dialog.close();
      this.release();
      if (!wasOpen || !returnFocus) return;
      const trigger = this.openedBy;
      this.focusFrame = requestAnimationFrame(() => {
        this.focusFrame = null;
        if (!this.isConnected || this.dialog.open || document.querySelector('dialog[open]') || !trigger?.isConnected || trigger.closest('[inert], [hidden]')) return;
        const active = document.activeElement;
        if (active && active !== document.body && !this.dialog.contains(active) && active.getClientRects().length) return;
        trigger.focus({ preventScroll: true });
      });
    }

    release() {
      clearTimeout(this.slowTimer);
      if (this.widget && this.inline && this.widget.parentElement !== this.inline) this.inline.append(this.widget);
      this.openedBy?.setAttribute('aria-expanded', 'false');
      if (!document.querySelector('marame-reviews dialog[open]')) document.body.classList.remove('marame-reviews-open');
    }

    viewInline() {
      this.finishHide(false, true);
      this.inline?.scrollIntoView({ block: 'start', behavior: 'auto' });
      const first = [...(this.widget?.querySelectorAll('button, a[href], select, input') || [])].find(element =>
        !element.closest('.jdgm-legacy-widget-content, [hidden], [inert]') && element.getClientRects().length && window.getComputedStyle(element).visibility !== 'hidden'
      );
      first?.focus({ preventScroll: true });
    }

    isBackdrop(event) {
      if (event.target !== this.dialog) return false;
      const rect = this.dialog.getBoundingClientRect();
      return event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom;
    }
  });
}
